import React from 'react';

type RoutePage = 'upload' | 'loading' | 'results' | 'history';

interface ErrorBoundaryProps {
  page: RoutePage;
  resetKey: string;
  content: RouteErrorContent;
  children: React.ReactNode;
}

interface RouteErrorContent {
  eyebrow: string;
  title: string;
  message: string;
  retryLabel: string;
  backLabel: string;
  primaryLabel: string;
  primaryHref: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class RouteErrorBoundaryImpl extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Route render failed', {
      page: this.props.page,
      error,
      componentStack: errorInfo.componentStack,
    });
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <RouteErrorFallback
          content={this.props.content}
          onRetry={() => this.setState({ error: null })}
        />
      );
    }

    return this.props.children;
  }
}

function RouteErrorFallback({
  content,
  onRetry,
}: {
  content: RouteErrorContent;
  onRetry: () => void;
}) {
  return (
    <section className="route-error-card animate-fade-in" role="alert">
      <span className="route-error-eyebrow">{content.eyebrow}</span>
      <h1 className="route-error-title">{content.title}</h1>
      <p className="route-error-message">{content.message}</p>

      <div className="route-error-actions">
        <button className="button button-primary" onClick={onRetry}>
          {content.retryLabel}
        </button>
        <button
          className="button button-secondary"
          onClick={() => window.history.back()}
        >
          {content.backLabel}
        </button>
        <button
          className="button button-secondary"
          onClick={() => window.location.assign(content.primaryHref)}
        >
          {content.primaryLabel}
        </button>
      </div>
    </section>
  );
}

export default function RouteErrorBoundary({
  page,
  resetKey,
  content,
  children,
}: {
  page: RoutePage;
  resetKey: string;
  content: RouteErrorContent;
  children: React.ReactNode;
}) {
  return (
    <RouteErrorBoundaryImpl page={page} resetKey={resetKey} content={content}>
      {children}
    </RouteErrorBoundaryImpl>
  );
}
